import { userType } from "@/propTypes";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useToggle } from "../../hooks/useToggle";
import AdGroupSelector from "../AdGroupSelector/AdGroupSelector";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import UserSelector from "../UserSelector/UserSelector";

import {
  useGetPermissionUsersExcludedQuery,
  useGetPermissionUsersQuery,
} from "@/services";

export const AddExcludeUsers = ({
  userAssignments,
  adGroups,
  adGroupAssignments,
  adGroupsLoading,
  setModel,
  entityType,
}) => {
  const [hidden, toggle] = useToggle(true);
  const [conflictedUser, setConflictedUser] = useState({});

  const { id: permissionId } = useParams();

  const { data: includedUsers, isFetching: includedUsersLoading } =
    useGetPermissionUsersQuery(permissionId, { skip: !permissionId });

  const { data: excludedUsers, isFetching: excludedUsersLoading } =
    useGetPermissionUsersExcludedQuery(permissionId, { skip: !permissionId });

  const setUserAssignments = (userAssignments) => {
    setModel({ userAssignments });
  };

  const setAdGroupAssignments = (adGroupAssignments) => {
    setModel({ adGroupAssignments });
  };

  // if we have default Users loaded, set them as default manual Users
  useEffect(() => {
    let defaultUserAssignments = [];

    if (includedUsers) {
      defaultUserAssignments = [
        ...includedUsers.map((user) => ({
          ...user,
          id: user.id,
          userId: user.id,
          exclude: false,
        })),
      ];
    }

    let defaultExcludedUsers = [];

    if (excludedUsers) {
      defaultExcludedUsers = [
        ...excludedUsers.map((user) => ({
          ...user,
          id: user.id,
          userId: user.id,
          exclude: true,
        })),
      ];
    }

    setUserAssignments([...defaultUserAssignments, ...defaultExcludedUsers]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includedUsers, excludedUsers]);

  // When a user suggestion is selected, add it to the list of manual Users if that user hasn't already been added
  const handleUserSuggestionSelected = (user, exclude) => {
    const userAlreadyExistsOnUserAssignments = userAssignments?.find(
      (userAssignment) =>
        userAssignment.id === user.id && userAssignment.exclude !== exclude
    );

    if (userAlreadyExistsOnUserAssignments) {
      user.Exclude = exclude;
      setConflictedUser(user);
      toggle();
    } else {
      setUserAssignments([
        ...(userAssignments ?? []),
        { ...user, userId: user.id, exclude },
      ]);
    }
  };

  const handleAdGroupSelected = (group) => {
    const existingAdGroupAssignments = adGroupAssignments ?? [];

    const adGroupAlreadyExistsOnAdGroupAssignments =
      existingAdGroupAssignments.find((adGroup) => adGroup.id === group.id);

    if (!adGroupAlreadyExistsOnAdGroupAssignments) {
      setAdGroupAssignments([...existingAdGroupAssignments, group.id]);
    }
  };

  const swapUser = () => {
    const notConflictedUsers = userAssignments.filter(
      ({ id }) => id !== conflictedUser.id
    );

    setUserAssignments([
      ...notConflictedUsers,
      {
        ...conflictedUser,
        userId: conflictedUser.id,
      },
    ]);

    setConflictedUser({});
    toggle();
  };

  const onUserAssignmentRemoved = (id) => {
    setUserAssignments(
      userAssignments.filter((userAssignment) => userAssignment.id !== id)
    );
  };

  const onAdGroupRemoved = (id) => {
    setAdGroupAssignments(
      adGroupAssignments.filter((adGroupAssignment) => adGroupAssignment !== id)
    );
  };

  return (
    <>
      <div className="pb-2">
        <AdGroupSelector
          name="add-adgroups-selector"
          cardTitleKey={`${entityType}.addAdGroupsTitle`}
          cardSubTitleKey={`${entityType}.addAdGroupsSubtitle`}
          adGroupsLoading={adGroupsLoading}
          onAdGroupSelected={(value) => {
            handleAdGroupSelected(value);
          }}
          existingAdGroups={adGroups}
          onAdGroupRemoved={onAdGroupRemoved}
          isEmailGroup={false}
        />
      </div>
      <div className="pb-2">
        <UserSelector
          name="add-users-selector"
          cardTitleKey={`${entityType}.addUsersCardTitle`}
          cardSubTitleKey={`${entityType}.addUsersCardSubtitle`}
          onUserSelected={(value) => {
            handleUserSuggestionSelected(value, false);
          }}
          usersLoading={includedUsersLoading}
          existingUsers={userAssignments?.filter(({ exclude }) => !exclude)}
          onUserRemoved={onUserAssignmentRemoved}
          isAddUser={true}
        />
      </div>
      <div className="pb-2">
        <UserSelector
          name="exclude-users-selector"
          cardTitleKey={`${entityType}.excludeUsersTitle`}
          cardSubTitleKey={`${entityType}.excludeUsersSubTitle`}
          usersLoading={excludedUsersLoading}
          onUserSelected={(value) => {
            handleUserSuggestionSelected(value, true);
          }}
          existingUsers={userAssignments?.filter(({ exclude }) => exclude)}
          onUserRemoved={onUserAssignmentRemoved}
          isAddUser={false}
        />
      </div>

      <ConfirmModal
        onSaveChanges={swapUser}
        hidden={hidden}
        toggle={toggle}
        headerTitleKey={`${entityType}.duplicateUserFoundTitle`}
        bodyTextKey={`${entityType}.duplicateUserFoundDescription`}
      />
    </>
  );
};

AddExcludeUsers.propTypes = {
  includedUsers: arrayOf(userType),
  adGroups: arrayOf(shape({ id: string })),
  adGroupAssignments: arrayOf(string),
  userAssignments: arrayOf(userType),
  adGroupsLoading: bool.isRequired,
  setModel: func.isRequired,
  entityType: string.isRequired,
};
